/** IMPORTS & CVs — where every imported / CV-requested person on this requirement stands (6 Oct 2026).
 *
 *  Answers "I imported 40 — how many sent their CV, who passed, who's invited?" per file, and is
 *  where the recruiter acts on it:
 *    - the CV screen setting (off / suggest / auto + the bar) — PATCH /requisitions/{id}/cv-screen
 *    - Ready to invite: CVs at or above the bar, invited with one click (billed, confirmed first)
 *    - Chase: re-send the CV link to everyone who hasn't replied
 *    - Screen waiting CVs: score CVs that arrived before the screen was on
 *  Data: GET /requisitions/{id}/imports (candidatesPipeline.ts). The CV-screen setting is always shown
 *  to people who can edit it (#238) — it used to be hidden until someone was imported, so a new
 *  requirement had no way to set it; the progress table still waits for someone to show. */
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSpreadsheet, Mail, RefreshCw, Send } from "lucide-react";
import type { CvScreen, CvScreenMode } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { ConfirmChargeModal } from "./ConfirmChargeModal.js";
import { serverMessage } from "./planUsage.js";
import { Dropdown } from "../../components/Dropdown.js";

type Stage = "requested" | "opened" | "expired" | "submitted" | "review" | "below" | "passed" | "invited" | "interviewed";

interface Person {
  candidacyId: string;
  name: string | null;
  email: string | null;
  batch: string;
  stage: Stage;
  score: number | null;
  scanId: string | null;
  requestStatus: string | null;
  lastRequestedAt: string | null;
}
interface Batch {
  label: string;
  firstAt: string;
  total: number;
  stages: Record<Stage, number>;
}
interface ImportsResponse {
  cvScreen: CvScreen;
  batches: Batch[];
  people: Person[];
}

/** Display order and wording. "Waiting" merges sent + opened: both mean "no CV yet". */
const COLUMNS: { key: string; label: string; stages: Stage[]; tip: string }[] = [
  { key: "waiting", label: "Waiting for CV", stages: ["requested", "opened"], tip: "Link sent (or opened), no CV yet" },
  { key: "expired", label: "Link expired", stages: ["expired"], tip: "The 14-day link ran out — chase to send a new one" },
  { key: "submitted", label: "CV in, not scored", stages: ["submitted"], tip: "CV received; not scored yet" },
  { key: "review", label: "Needs review", stages: ["review"], tip: "Couldn't be scored automatically (unreadable CV or AI unavailable) — not charged" },
  { key: "below", label: "Below the bar", stages: ["below"], tip: "Scored under the bar — nobody is rejected; you decide" },
  { key: "passed", label: "Ready to invite", stages: ["passed"], tip: "Scored at or above the bar, not invited yet" },
  { key: "invited", label: "Invited", stages: ["invited"], tip: "Interview link sent" },
  { key: "interviewed", label: "Interviewed", stages: ["interviewed"], tip: "Interview scored" },
];

const MODE_LABEL: Record<CvScreenMode, string> = {
  off: "Off — I review CVs myself",
  suggest: "Score, I approve invites",
  auto: "Score and invite automatically",
};

export function ImportsPanel({ reqId, title, canEdit }: { reqId: string; title: string; canEdit: boolean }): ReactNode {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["requisition-imports", reqId],
    queryFn: () => api.get<ImportsResponse>(`/requisitions/${reqId}/imports`),
    refetchInterval: 30_000, // CVs arrive without a page action
  });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [show, setShow] = useState<string | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["requisition-imports", reqId] });
    void qc.invalidateQueries({ predicate: (query) => query.queryKey.includes(reqId) });
    void qc.invalidateQueries({ queryKey: ["org-candidates"] });
  };

  const setScreen = useMutation({
    mutationFn: (next: CvScreen) => api.patch<{ cvScreen: CvScreen }>(`/requisitions/${reqId}/cv-screen`, next),
    onSuccess: () => refresh(),
  });
  const invite = useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ invited: unknown[]; skipped: unknown[] }>(`/requisitions/${reqId}/candidates/invite`, { candidacyIds: ids }),
    onSuccess: (r) => {
      setConfirming(false);
      setPicked(new Set());
      setNote(
        `${r.invited.length} interview link${r.invited.length === 1 ? "" : "s"} sent.` +
          (r.skipped.length ? ` ${r.skipped.length} already invited — skipped, not charged.` : ""),
      );
      refresh();
    },
  });
  const chase = useMutation({
    mutationFn: () => api.post<{ chased: number; skippedRecent: number; skippedNoEmail: number }>(`/requisitions/${reqId}/imports/chase`, {}),
    onSuccess: (r) => {
      setNote(
        `${r.chased} reminder${r.chased === 1 ? "" : "s"} sent with a fresh link.` +
          (r.skippedRecent ? ` ${r.skippedRecent} were emailed in the last 12 hours and left alone.` : "") +
          (r.skippedNoEmail ? ` ${r.skippedNoEmail} have no email address.` : ""),
      );
      refresh();
    },
  });
  const screenNow = useMutation({
    mutationFn: () => api.post<{ screened: number; results: Record<string, number> }>(`/requisitions/${reqId}/imports/screen`, {}),
    onSuccess: (r) => {
      const t = r.results;
      setNote(
        `${r.screened} CV${r.screened === 1 ? "" : "s"} screened` +
          [
            t.invited ? `${t.invited} invited` : "",
            t.passed ? `${t.passed} ready to invite` : "",
            t.below ? `${t.below} below the bar` : "",
            t.review ? `${t.review} need review` : "",
            t.no_scan_credits ? `${t.no_scan_credits} skipped — no resume scans left` : "",
            t.no_interview_credits ? `${t.no_interview_credits} passed but no AI interviews left to send` : "",
          ]
            .filter(Boolean)
            .map((s, i) => (i === 0 ? `: ${s}` : `, ${s}`))
            .join("") +
          ".",
      );
      refresh();
    },
  });

  const data = q.data;
  const ready = useMemo(() => (data?.people ?? []).filter((p) => p.stage === "passed").sort((a, b) => (b.score ?? 0) - (a.score ?? 0)), [data]);
  const waitingCount = (data?.people ?? []).filter((p) => ["requested", "opened", "expired"].includes(p.stage)).length;
  const unscored = (data?.people ?? []).filter((p) => p.stage === "submitted").length;
  const shown = show ? (data?.people ?? []).filter((p) => COLUMNS.find((c) => c.key === show)?.stages.includes(p.stage)) : [];

  if (q.isLoading || !data) return null;
  const screen = data.cvScreen;
  if (data.people.length === 0 && screen.mode === "off" && !canEdit) return null;
  const thresholds = [...new Set([50, 55, 60, 65, 70, 75, 80, 85, 90, screen.threshold])].sort((a, b) => a - b);

  const chosen = ready.filter((p) => picked.has(p.candidacyId));
  const busy = setScreen.isPending || invite.isPending || chase.isPending || screenNow.isPending;

  return (
    <section className="card t1-imp" aria-label="Imports and CVs">
      <div className="t1-card-h">
        <span className="t1-ico" aria-hidden="true">
          <FileSpreadsheet />
        </span>
        <div>
          <h2>Imports &amp; CVs</h2>
          <p>Everyone asked for their CV on this requirement — by file — and what happened when it came back.</p>
        </div>
      </div>

      <div className="t1-imp-screen">
        <div className="t1-imp-field">
          <span>When a CV comes back</span>
          <Dropdown<CvScreenMode>
            ariaLabel="When a CV comes back"
            value={screen.mode}
            disabled={!canEdit || busy}
            options={(["suggest", "auto", "off"] as const).map((m) => ({ value: m, label: MODE_LABEL[m] }))}
            onChange={(mode) => setScreen.mutate({ ...screen, mode })}
          />
        </div>
        <div className="t1-imp-field">
          <span>Match bar</span>
          <Dropdown<number>
            ariaLabel="Match bar"
            value={screen.threshold}
            disabled={!canEdit || busy}
            options={thresholds.map((t) => ({ value: t, label: `${t}% match or more` }))}
            onChange={(threshold) => setScreen.mutate({ ...screen, threshold })}
          />
        </div>
        <p className="t1-imp-help">
          {screen.mode === "off"
            ? `CVs land unscored. Run matching from the Candidates page when you want a score. The ${screen.threshold}% bar applies once scoring is switched on.`
            : `Each CV is scored as it arrives (one resume scan). At ${screen.threshold}%+ ${
                screen.mode === "auto" ? "the interview link is emailed automatically (one AI interview)" : "it waits below for you to invite"
              }. Below the bar nobody is rejected or emailed.`}
        </p>
        {setScreen.isError && <p className="t1-imp-err">{serverMessage(setScreen.error, "Couldn’t change the CV screen.")}</p>}
      </div>

      {data.batches.length > 0 && (
        <div className="t1-imp-table" role="table" aria-label="Progress by file">
          <div className="t1-imp-tr t1-imp-th" role="row">
            <span role="columnheader">File</span>
            <span role="columnheader" className="tnum">People</span>
            {COLUMNS.map((c) => (
              <span key={c.key} role="columnheader" data-tip={c.tip}>{c.label}</span>
            ))}
          </div>
          {data.batches.map((b) => (
            <div key={b.label} className="t1-imp-tr" role="row">
              <span role="cell" className="t1-imp-file" title={b.label}>
                <b>{b.label}</b>
                <small>{new Date(b.firstAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</small>
              </span>
              <span role="cell" className="tnum">{b.total}</span>
              {COLUMNS.map((c) => {
                const n = c.stages.reduce((sum, s) => sum + (b.stages[s] ?? 0), 0);
                return (
                  <span key={c.key} role="cell" className={`tnum${n ? "" : " is-zero"}`} data-label={c.label}>
                    {n}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <div className="t1-imp-actions">
        {COLUMNS.map((c) => {
          const n = data.people.filter((p) => c.stages.includes(p.stage)).length;
          if (!n) return null;
          return (
            <button key={c.key} type="button" className={`t1-imp-chip${show === c.key ? " is-on" : ""}`} onClick={() => setShow(show === c.key ? null : c.key)}>
              {c.label} <b>{n}</b>
            </button>
          );
        })}
        <span className="t1-imp-spacer" />
        {waitingCount > 0 && canEdit && (
          <button type="button" className="btn" disabled={busy} onClick={() => chase.mutate()}>
            <Mail aria-hidden="true" /> {chase.isPending ? "Sending…" : `Chase ${waitingCount} without a CV`}
          </button>
        )}
        {unscored > 0 && screen.mode !== "off" && canEdit && (
          <button type="button" className="btn" disabled={busy} onClick={() => screenNow.mutate()}>
            <RefreshCw aria-hidden="true" /> {screenNow.isPending ? "Scoring…" : `Screen ${unscored} waiting CV${unscored === 1 ? "" : "s"}`}
          </button>
        )}
      </div>
      {note && <p className="t1-imp-note" role="status">{note}</p>}
      {(chase.isError || screenNow.isError) && (
        <p className="t1-imp-err">{serverMessage(chase.error ?? screenNow.error, "That didn’t work — please try again.")}</p>
      )}

      {show && show !== "passed" && (
        <ul className="t1-imp-list">
          {shown.map((p) => (
            <li key={p.candidacyId}>
              <b>{p.name ?? "Unnamed"}</b>
              <span>{p.email ?? "no email"}</span>
              {p.score !== null && <span className="tnum">{Math.round(p.score)}%</span>}
            </li>
          ))}
        </ul>
      )}

      {ready.length > 0 && (show === null || show === "passed") && (
        <div className="t1-imp-ready">
          <div className="t1-imp-ready-h">
            <h3>Ready to invite ({ready.length})</h3>
            <label className="t1-imp-check">
              <input
                type="checkbox"
                checked={chosen.length === ready.length}
                onChange={() => setPicked(chosen.length === ready.length ? new Set() : new Set(ready.map((p) => p.candidacyId)))}
              />{" "}
              Select all
            </label>
            {canEdit && (
              <button type="button" className="btn primary" disabled={!chosen.length || busy} onClick={() => setConfirming(true)}>
                <Send aria-hidden="true" /> Invite {chosen.length || ""}
              </button>
            )}
          </div>
          <ul className="t1-imp-list">
            {ready.map((p) => (
              <li key={p.candidacyId}>
                <label className="t1-imp-check">
                  <input
                    type="checkbox"
                    checked={picked.has(p.candidacyId)}
                    onChange={() =>
                      setPicked((s) => {
                        const n = new Set(s);
                        if (n.has(p.candidacyId)) n.delete(p.candidacyId);
                        else n.add(p.candidacyId);
                        return n;
                      })
                    }
                    aria-label={`Select ${p.name ?? p.email ?? "candidate"}`}
                  />
                  <b>{p.name ?? "Unnamed"}</b>
                </label>
                <span>{p.email ?? "no email — you’ll get a link to share"}</span>
                <span className="tnum t1-imp-score">{p.score !== null ? `${Math.round(p.score)}%` : "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {confirming && (
        <ConfirmChargeModal
          title="Confirm & send interviews"
          subtitle={`${chosen.length} candidate${chosen.length === 1 ? "" : "s"} will be emailed a verified AI interview link for ${title}.`}
          unit="interview"
          count={chosen.length}
          lines={[]}
          confirmLabel={chosen.length === 1 ? "Send interview" : `Send ${chosen.length} interviews`}
          busy={invite.isPending}
          error={invite.isError ? serverMessage(invite.error, "Couldn’t send those interviews — please try again.") : null}
          onConfirm={() => invite.mutate(chosen.map((p) => p.candidacyId))}
          onCancel={() => !invite.isPending && (invite.reset(), setConfirming(false))}
        />
      )}
    </section>
  );
}
